import React, { useMemo } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

// W-0937 — Status do Imóvel (RN-03/RN-05). Letra enviada ao backend (CSV) + label verbatim do ERP.
// Siglas de exibição no grid: D->DSP, A->ALG, B->BLQ, V->VEN, P->PVD, R->RSV, O->RAP.
export const STATUS_IMOVEL = [
  { letra: 'A', label: 'Alugado' },
  { letra: 'R', label: 'Reservado' },
  { letra: 'V', label: 'Vendido' },
  { letra: 'B', label: 'Bloqueado' },
  { letra: 'P', label: 'Pré Venda' },
  { letra: 'D', label: 'Disponível' },
  { letra: 'O', label: 'Prop.Rápida' },
];

// Fases do controle de contratos (1..12).
const FASES_CONTRATO = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// Pagamento (RN-06): 0=Financiados, 1=Quitados, 2=Todos.
const OPCOES_PAGAMENTO = [
  { value: 0, label: 'Financiados' },
  { value: 1, label: 'Quitados' },
  { value: 2, label: 'Todos' },
];

const construirValoresIniciais = () => ({
  codigoGrupoEmpresa: null,
  codigoGrupoGE: null,
  codigoFamilia: null,
  // Default: todos os status marcados.
  status: STATUS_IMOVEL.map((s) => s.letra),
  // Default: todas as 12 fases marcadas.
  fasesContrato: FASES_CONTRATO.slice(),
  tipoPagamento: 2,
  historicoCorrecoes: false,
  historicoMovimento: false,
});

const FiltrosHistoricoImoveis = ({
  gruposEmpresa,
  gruposGe,
  empreendimentos,
  carregandoEmpreendimentos,
  onGeChange,
  onSubmit,
  onCancelar,
  submitting,
}) => {
  // Backend retorna snake_case. GrupoEmpresaLookup: codigo_grupo_empresa/apelido.
  const opcoesGe = useMemo(
    () =>
      (gruposEmpresa || []).map((item) => ({
        value: item.codigo_grupo_empresa,
        label: `${item.codigo_grupo_empresa} - ${item.apelido}`,
      })),
    [gruposEmpresa]
  );

  // GrupoGELookup: codigo/descricao.
  const opcoesGrupoGe = useMemo(
    () =>
      (gruposGe || []).map((item) => ({
        value: item.codigo,
        label: `${item.codigo} - ${item.descricao}`,
      })),
    [gruposGe]
  );

  // EmpreendimentoLookup: codigo_familia/nome_da_familia.
  const opcoesEmpreendimento = useMemo(
    () =>
      (empreendimentos || []).map((item) => ({
        value: item.codigo_familia,
        label: `${item.codigo_familia} - ${item.nome_da_familia}`,
      })),
    [empreendimentos]
  );

  const valorSelecionado = (opts, val) =>
    opts.find((o) => o.value === val) || null;

  return (
    <Formik
      initialValues={construirValoresIniciais()}
      onSubmit={(values) => {
        // Validações de submit (RN-04/RN-05).
        if (!values.status || values.status.length === 0) {
          toast.error('Nenhum Status selecionado!');
          return;
        }
        if (!values.fasesContrato || values.fasesContrato.length === 0) {
          toast.error('Nenhuma Fase do controle de contratos escolhida');
          return;
        }
        onSubmit({
          codigoGrupoEmpresa: values.codigoGrupoEmpresa,
          codigoGrupoGE: values.codigoGrupoGE,
          codigoFamilia: values.codigoFamilia,
          status: values.status,
          fasesContrato: values.fasesContrato,
          tipoPagamento: values.tipoPagamento,
          historicoCorrecoes: values.historicoCorrecoes,
          historicoMovimento: values.historicoMovimento,
        });
      }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm }) => {
        const erroMutex =
          values.codigoGrupoEmpresa && values.codigoGrupoGE
            ? 'Selecione G.E. ou Grupo de G.E., não ambos.'
            : null;

        // RN-02: GE e Grupo de GE mutuamente exclusivos — trocar um limpa o
        // outro e o empreendimento, e recarrega o combo de empreendimentos.
        const handleGeSelect = (selecionado) => {
          const novoValor = selecionado ? selecionado.value : null;
          setFieldValue('codigoGrupoEmpresa', novoValor);
          setFieldValue('codigoGrupoGE', null);
          setFieldValue('codigoFamilia', null);
          onGeChange(novoValor, null);
        };

        const handleGrupoGeSelect = (selecionado) => {
          const novoValor = selecionado ? selecionado.value : null;
          setFieldValue('codigoGrupoGE', novoValor);
          setFieldValue('codigoGrupoEmpresa', null);
          setFieldValue('codigoFamilia', null);
          onGeChange(null, novoValor);
        };

        // Status — master "Todos" marca/desmarca os 7.
        const todosStatusMarcados =
          values.status.length === STATUS_IMOVEL.length;

        const alternarTodosStatus = (marcado) => {
          setFieldValue(
            'status',
            marcado ? STATUS_IMOVEL.map((s) => s.letra) : []
          );
        };

        const alternarStatus = (letra, marcado) => {
          const atual = values.status || [];
          const proximo = marcado
            ? atual.concat([letra])
            : atual.filter((l) => l !== letra);
          setFieldValue('status', proximo);
        };

        // Fases — master "Todos" auto-desmarca se qualquer fase desmarcada.
        const todasFasesMarcadas =
          values.fasesContrato.length === FASES_CONTRATO.length;

        const alternarTodasFases = (marcado) => {
          setFieldValue('fasesContrato', marcado ? FASES_CONTRATO.slice() : []);
        };

        const alternarFase = (fase, marcado) => {
          const atual = values.fasesContrato || [];
          const proximo = marcado
            ? atual.concat([fase])
            : atual.filter((f) => f !== fase);
          setFieldValue('fasesContrato', proximo);
        };

        const handleResetar = () => {
          resetForm({ values: construirValoresIniciais() });
          onCancelar();
        };

        return (
          <Card className="mb-3 shadow-sm">
            <Card.Header className="bg-light">
              <strong>Filtros</strong>
            </Card.Header>
            <Card.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">
                      Grupo de Empresa (G.E.)
                    </Form.Label>
                    <Select
                      placeholder="Selecione..."
                      isClearable
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesGe}
                      value={valorSelecionado(opcoesGe, values.codigoGrupoEmpresa)}
                      onChange={handleGeSelect}
                      noOptionsMessage={() => 'Nenhuma GE disponível'}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">Grupo de G.E.</Form.Label>
                    <Select
                      placeholder="Selecione..."
                      isClearable
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesGrupoGe}
                      value={valorSelecionado(opcoesGrupoGe, values.codigoGrupoGE)}
                      onChange={handleGrupoGeSelect}
                      noOptionsMessage={() => 'Nenhum Grupo de G.E. disponível'}
                    />
                    {erroMutex && (
                      <Form.Text className="text-danger">{erroMutex}</Form.Text>
                    )}
                  </Col>
                  <Col xs={12} md={12} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">Empreendimento</Form.Label>
                    <Select
                      placeholder={
                        values.codigoGrupoEmpresa || values.codigoGrupoGE
                          ? 'Selecione...'
                          : 'Selecione uma G.E. ou Grupo de G.E. primeiro'
                      }
                      isClearable
                      isDisabled={
                        (!values.codigoGrupoEmpresa && !values.codigoGrupoGE) ||
                        carregandoEmpreendimentos
                      }
                      isLoading={carregandoEmpreendimentos}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesEmpreendimento}
                      value={valorSelecionado(
                        opcoesEmpreendimento,
                        values.codigoFamilia
                      )}
                      onChange={(selecionado) =>
                        setFieldValue(
                          'codigoFamilia',
                          selecionado ? selecionado.value : null
                        )
                      }
                      noOptionsMessage={() => 'Nenhum empreendimento disponível'}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Card>
                      <Card.Header className="py-2">
                        <strong className="small">Status do Imóvel</strong>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Form.Check
                          type="checkbox"
                          id="status-todos"
                          label="Todos"
                          className="font-weight-bold mb-2"
                          checked={todosStatusMarcados}
                          onChange={(e) => alternarTodosStatus(e.target.checked)}
                        />
                        <div className="d-flex flex-wrap">
                          {STATUS_IMOVEL.map((s) => (
                            <Form.Check
                              key={`status-${s.letra}`}
                              type="checkbox"
                              id={`status-${s.letra}`}
                              label={s.label}
                              className="mr-3"
                              checked={values.status.indexOf(s.letra) !== -1}
                              onChange={(e) =>
                                alternarStatus(s.letra, e.target.checked)
                              }
                            />
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Card>
                      <Card.Header className="py-2">
                        <strong className="small">Controle de Contratos</strong>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Form.Check
                          type="checkbox"
                          id="fase-todos"
                          label="Todos"
                          className="font-weight-bold mb-2"
                          checked={todasFasesMarcadas}
                          onChange={(e) => alternarTodasFases(e.target.checked)}
                        />
                        <div className="d-flex flex-wrap">
                          {FASES_CONTRATO.map((fase) => (
                            <Form.Check
                              key={`fase-${fase}`}
                              type="checkbox"
                              id={`fase-${fase}`}
                              label={String(fase)}
                              className="mr-3"
                              checked={values.fasesContrato.indexOf(fase) !== -1}
                              onChange={(e) =>
                                alternarFase(fase, e.target.checked)
                              }
                            />
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="small mb-1 d-block">
                      Pagamento
                    </Form.Label>
                    {OPCOES_PAGAMENTO.map((op) => (
                      <Form.Check
                        key={`pgto-${op.value}`}
                        inline
                        type="radio"
                        id={`pgto-${op.value}`}
                        name="tipoPagamento"
                        label={op.label}
                        checked={values.tipoPagamento === op.value}
                        onChange={() => setFieldValue('tipoPagamento', op.value)}
                      />
                    ))}
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="small mb-1 d-block">
                      Impressão
                    </Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="historico-correcoes"
                      label="Histórico Correções"
                      checked={values.historicoCorrecoes}
                      onChange={(e) =>
                        setFieldValue('historicoCorrecoes', e.target.checked)
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      id="historico-movimento"
                      label="Histórico Movimento"
                      checked={values.historicoMovimento}
                      onChange={(e) =>
                        setFieldValue('historicoMovimento', e.target.checked)
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col className="d-flex justify-content-end">
                    <Button
                      type="button"
                      variant="secondary"
                      className="mr-2"
                      onClick={handleResetar}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting || !!erroMutex}
                    >
                      {submitting ? 'Gerando...' : 'Gerar'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        );
      }}
    </Formik>
  );
};

export default FiltrosHistoricoImoveis;
